import { Component, EventEmitter, OnInit } from '@angular/core';
import { GridApi } from 'ag-grid-enterprise';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ToggleButtonObjectType } from '@niceltd/cxone-components/toggle-button-group/src/lib/toggle-button-group.component';
import { SelectedDateRange } from './cxone-etl-data-recalculation.model';
import { SaveRecalculationDataObj } from './cxone-etl-recalculation-modal.model';
import { CxoneEtlRecalculationService } from './cxone-etl-recalculation.service';
import { DatePipe } from '@angular/common';
import { ToastrManager } from '@niceltd/cxone-components/toastr';
import { MessageModalComponent, MessageTypes } from '@niceltd/cxone-components/modal';

@Component({
    selector: 'cv-cxone-etl-recalculation-modal',
    templateUrl: './cxone-etl-recalculation-modal.component.html',
    styleUrls: ['./cxone-etl-recalculation-modal.component.scss']
})
export class CxoneEtlRecalculationModalComponent implements OnInit {
    public listMetricView = "";
    public searchInput = '';
    public listReportingSetView = "";
    public searchPlaceholderText = '';
    public headerTitle: string;
    public listViewLabel: string;
    public detailedViewLabel: string;
    public isDefinitionSelected = false;
    public recalculateLabel: string;
    public cancelLabel: string;
    public dateRangeObj: SelectedDateRange;
    public isLocalizationSectionLoaded: boolean;
    public gridApi: GridApi;
    public totalCount: string;
    public selectedMetricDefinitions: string[];
    public selectedReportingSetDefinitions: string[];
    public reportingSetsView: boolean;
    public metricsView: boolean;
    public dateRangeInfoMsg = '';
    public customDateRange: { maxDate: Date };
    public startDate: string;
    public endDate: string;
    public reportingSetsGridView: boolean;
    public metricsGridView: boolean;
    public viewOptions: ToggleButtonObjectType[] = [];
    public expandReportingSetAccordian = false;
    public expandMetricsAccordian = false;
    public metricsInProgress = [];
    public reportingSetsInProgress = [];
    public inProgressItemsInfo: string;
    public warningMessage: string;
    public showWarningMsg = false;
    public entitiesMaxSelectionLimit = 10;
    public showToggleButtons = false;
    public reloadReportingSetGrid: EventEmitter<any> = new EventEmitter();
    public reloadMetricsGrid: EventEmitter<any> = new EventEmitter();
    private metricsText: string;
    private reportingSetsText: string;

    constructor(
        public dialogRef: DynamicDialogRef,
        public _dialogConfig: DynamicDialogConfig,
        private cvLocalizationService: CVLocalizationService,
        private cxoneEtlRecalculation: CxoneEtlRecalculationService,
        private datePipe: DatePipe,
        private toastrManager: ToastrManager,
        private dialogService: DialogService
    ) { }

    async ngOnInit(): Promise<void> {
        if (this._dialogConfig.data) {
            this.metricsView = this._dialogConfig.data.metricsView;
            this.reportingSetsView = this._dialogConfig.data.reportingSetsView;
            this.metricsInProgress = this.metricsView ? this._dialogConfig.data.metricsInProgress : [];
            this.reportingSetsInProgress = this.reportingSetsView ? this._dialogConfig.data.reportingSetsInProgress : [];
            this.metricsGridView = this.metricsView;
            this.reportingSetsGridView = this.reportingSetsView;
            this.showToggleButtons = this.metricsView && this.reportingSetsView;
        }
        if (!this.metricsView) {
            this.expandReportingSetAccordian = true;
        } else {
            this.expandMetricsAccordian = true;
            this.expandReportingSetAccordian = false;
        }

        await this.loadLocalization();
        this.isLocalizationSectionLoaded = true;
        const currentDate = new Date();
        // Set the date to the 1st day of the current month
        this.dateRangeObj = {
            fromDate: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
            toDate: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
        };
        this.setSelectableDateRange();

        sendMessageCXOne({hasNonSavedChanges: true});
    }

    onDateRangeChanged(selectedDate: any) {
        // Always set the start date to 1st of the same month and end date to last date of the selected month
        selectedDate.fromDate = new Date(selectedDate.fromDate);
        selectedDate.fromDate.setDate(1);
        selectedDate.toDate = new Date(selectedDate.toDate.getFullYear(), selectedDate.toDate.getMonth() + 1, 0);
        const startDateObject = new Date(selectedDate.fromDate);
        const endDateObject = new Date(selectedDate.toDate);
        this.startDate = this.datePipe.transform(startDateObject, 'yyyy-MM-dd');
        this.endDate = this.datePipe.transform(endDateObject, 'yyyy-MM-dd');
    }

    setSelectableDateRange() {
        // set max date for date range selection(future dates not allowed)
        const currentDate = new Date();
        const maxDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        this.customDateRange = { maxDate };
    }

    getTotalRowsCount(data: string) {
        this.totalCount = data;
    }

    getSelectedMetricList(data: string[]) {
        const newData = [...data];
        this.selectedMetricDefinitions = newData;
        this.checkDefinitionsLimit();
        this.cxoneEtlRecalculation.publishMetricListChange(this.selectedMetricDefinitions);
        this.cxoneEtlRecalculation.setEmittedValue = undefined;
        this.reloadMetricsGrid.emit();
    }

    getSelectedReportingSetList(data: string[]) {
        const newData = [...data];
        this.selectedReportingSetDefinitions = newData;
        this.checkDefinitionsLimit();
        this.cxoneEtlRecalculation.publishReportingSetListChange(this.selectedReportingSetDefinitions);
        this.cxoneEtlRecalculation.setEmittedValue = undefined;
        this.reloadReportingSetGrid.emit();
    }

    getGridData(data) {
        this.gridApi = data;
    }

    public onSearchCriteriaChange = (searchCriteria: string) => {
        this.searchInput = searchCriteria;
    };

    async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('ETL_Recalculation_Modal');
        }
        catch {
            console.error('Failed to load localization for ETL_Recalculation_Modal');
        }
        finally {
            this.metricsText = this.getLocalizationString('MetricsTab', 'Metrics');
            this.reportingSetsText = this.getLocalizationString('ReportingSetsTab', 'Reporting Sets');
            this.searchPlaceholderText = this.getLocalizationString("Search", "Search...");
            this.listMetricView = this.getLocalizationString("List_View_Header", "Metrics");
            this.listReportingSetView = this.getLocalizationString("Detailed_View_Header", "Reporting Sets");
            this.listViewLabel = this.getLocalizationString("List_View", "List View");
            this.detailedViewLabel = this.getLocalizationString("Detailed_View", "Detailed View");
            this.recalculateLabel = this.getLocalizationString("Recalculate", "Recalculate");
            this.cancelLabel = this.getLocalizationString("Cancel", "Cancel");
            this.headerTitle = this.getLocalizationString("Recalculate_Data", "Recalculate Data");
            this.dateRangeInfoMsg = this.getLocalizationString(
                "Date_Range_Info",
                "Recalculation starts on the 1st of each month. Selection covers entire month and can be for more than a month."
            );
            this.inProgressItemsInfo = this.getLocalizationString(
                "InProgress_Info",
                "The entities that are being recalculated are not be available for selection."
            );
            this.warningMessage = this.getLocalizationString("Warning_Message", "A maximum of 10 entities can be recalculated at a time.");
            if (this.metricsView) {
                this.viewOptions.push({ id: 'Metrics', name: this.metricsText });
            }
            if (this.reportingSetsView) {
                this.viewOptions.push({ id: 'ReportingSets', name: this.reportingSetsText });
            }
        }
    }

    save() {
        this.saveRecalculationData();
        sendMessageCXOne({hasNonSavedChanges: false});
    }

    public async saveRecalculationData() {
        const recalculationData: SaveRecalculationDataObj = {
            startDate: this.startDate,
            endDate: this.endDate,
            metricIds: this.selectedMetricDefinitions ? this.selectedMetricDefinitions : [],
            reportingSetIds: this.selectedReportingSetDefinitions ? this.selectedReportingSetDefinitions : [],
            hierarchyIds: []
        };
        try {
            await this.cxoneEtlRecalculation.saveRecalculationData(recalculationData);
            this.cxoneEtlRecalculation.clearLastMetricChange();
            this.cxoneEtlRecalculation.clearLastReportingSetChange();
            this.dialogRef.close(recalculationData);
        } catch (error) {
            this.toastrManager.showToaster('error',
                this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
        } finally {
            this.searchInput = '';
        }
    }

    cancel() {
        if ((!this.selectedMetricDefinitions || this.selectedMetricDefinitions.length === 0)
            && (!this.selectedReportingSetDefinitions || this.selectedReportingSetDefinitions.length === 0)) {
            this.cxoneEtlRecalculation.clearLastMetricChange();
            this.cxoneEtlRecalculation.clearLastReportingSetChange();
            this.dialogRef.close();
            sendMessageCXOne({ hasNonSavedChanges: false });
            return;
        }

        const confirmationTitle = this.cvLocalizationService.getCachedText(
            "ETL_Recalculation_Modal",
            "Cancel_Recalculation",
            "Unsaved Data"
        );

        const confirmationMessage = this.cvLocalizationService.getCachedText(
            "ETL_Recalculation_Modal",
            "Cancel_Recalculation_Message",
            "Your changes will be lost. Are you sure you want to cancel this operation?"
        );

        const confirmationRef: DynamicDialogRef = this.dialogService.open(
            MessageModalComponent,
            {
                width: "420px",
                height: "180px",
                data: {
                    title: confirmationTitle,
                    message: confirmationMessage,
                    type: "system-warning" as MessageTypes,
                    closeButtonText: this.cvLocalizationService.getCachedText("ETL_Recalculation_Modal", "Stay", "Stay"),
                    primaryButtonText: this.cvLocalizationService.getCachedText("ETL_Recalculation_Modal", "Leave", "Leave"),
                },
            }
        );

        confirmationRef.onClose.subscribe((result) => {
            if (result) {
                // Cancel recalculation and close the modal
                this.cxoneEtlRecalculation.clearLastMetricChange();
                this.cxoneEtlRecalculation.clearLastReportingSetChange();
                this.dialogRef.close();
                sendMessageCXOne({ hasNonSavedChanges: false });
            }
        });
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("ETL_Recalculation_Modal", key, defaultValue);
        return baseString;
    }

    public onClickToggle(event) {
        this.searchInput = "";
        if (event.id === 'Metrics') {
            this.metricsGridView = true;
            this.reportingSetsGridView = false;
        }
        else {
            this.metricsGridView = false;
            this.reportingSetsGridView = true;
        }
    }

    public onClickAccordian(event) {
        this.expandMetricsAccordian = !this.expandMetricsAccordian;
        this.expandReportingSetAccordian = !this.expandReportingSetAccordian;
    }

    private checkDefinitionsLimit(): void {
        if ((this.selectedMetricDefinitions && this.selectedMetricDefinitions.length !== 0) ||
            (this.selectedReportingSetDefinitions && this.selectedReportingSetDefinitions.length !== 0)) {
            // Check if the combined length does not exceed 10
            if ((this.selectedMetricDefinitions ? this.selectedMetricDefinitions.length : 0) +
                (this.selectedReportingSetDefinitions ? this.selectedReportingSetDefinitions.length : 0) +
                (this.metricsInProgress ? this.metricsInProgress.length : 0) +
                (this.reportingSetsInProgress ? this.reportingSetsInProgress.length : 0) <= this.entitiesMaxSelectionLimit) {
                this.isDefinitionSelected = true;
                this.showWarningMsg = false;
            } else {
                this.isDefinitionSelected = false;
                this.showWarningMsg = true;
            }
        } else {
            this.isDefinitionSelected = false;
            this.showWarningMsg = false;
        }
    }
}
